import { useEffect, useState } from "react";
import { X } from "lucide-react";

function ServicePrice({ service }) {
  if (service.priceFrom) {
    return (
      <>
        <span className="text-[10px] uppercase tracking-[0.22em] text-white/75">
          Starting at
        </span>
        <span className="font-display font-bold text-3xl text-[#f4d774]">
          {service.priceFrom}
        </span>
        <span className="text-xs text-white/75">
          up to{" "}
          <span className="text-white font-semibold">{service.priceTo}</span>
        </span>
      </>
    );
  }

  if (service.price) {
    return (
      <span className="font-display font-bold text-3xl text-[#f4d774]">
        {service.price}
      </span>
    );
  }

  return <span className="text-sm text-white/85">Request a quote</span>;
}

/**
 * Liquid-glass popover with a "potion bubble" entrance:
 * the bubble rises from the bottom + scales up, and reverses on close.
 * `will-change: transform` hints the browser to GPU-accelerate
 * (modern Chrome handles transform + backdrop-filter without the legacy flash).
 */
export default function AddonModal({ service, originRect, onClose, IconComp }) {
  const [mounted, setMounted] = useState(false);
  const [show, setShow] = useState(false);
  const [origin, setOrigin] = useState(null);

  // Lifecycle: mount → next frame → show
  useEffect(() => {
    if (service) {
      // Capture origin transform BEFORE mount so the very first paint is at the card.
      if (originRect) {
        const winW = window.innerWidth;
        const winH = window.innerHeight;
        // Use a target reference width matching the dialog max-w-md (~448px capped to padding).
        const dialogMaxW = Math.min(448, winW - 32);
        const cardCenterX = originRect.left + originRect.width / 2;
        const cardCenterY = originRect.top + originRect.height / 2;
        setOrigin({
          translateX: cardCenterX - winW / 2,
          translateY: cardCenterY - winH / 2,
          scale: originRect.width / dialogMaxW,
        });
      } else {
        setOrigin(null);
      }
      setMounted(true);
      const id = requestAnimationFrame(() =>
        requestAnimationFrame(() => setShow(true))
      );
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      const onKey = (e) => e.key === "Escape" && handleClose();
      window.addEventListener("keydown", onKey);
      return () => {
        cancelAnimationFrame(id);
        document.body.style.overflow = prev;
        window.removeEventListener("keydown", onKey);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service]);

  const handleClose = () => {
    setShow(false);
    // wait for the bubble to fly back to the originating card
    setTimeout(() => {
      setMounted(false);
      onClose();
    }, 400);
  };

  if (!mounted || !service) return null;

  // Compute transform string outside JSX for readability
  let transformValue;
  if (show) {
    transformValue = "translate(0,0) scale(1)";
  } else if (origin) {
    transformValue = `translate(${origin.translateX}px, ${origin.translateY}px) scale(${origin.scale})`;
  } else {
    transformValue = "translateY(48%) scale(0.32)";
  }

  return (
    <div
      data-testid="addon-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
    >
      {/* Backdrop — light dim + soft blur, keeps page visible */}
      <button
        aria-label="Close"
        onClick={handleClose}
        className="absolute inset-0 cursor-default bg-black/30"
        style={{
          opacity: show ? 1 : 0,
          transition: "opacity 280ms ease-out",
          backdropFilter: "blur(8px) saturate(110%)",
          WebkitBackdropFilter: "blur(8px) saturate(110%)",
        }}
      />

      {/* Potion-bubble wrapper drives the scale + rise animation. */}
      <div
        className="relative w-full max-w-md"
        style={{
          opacity: show ? 1 : 0,
          transform: transformValue,
          transformOrigin: "center center",
          transition: show
            ? "transform 540ms cubic-bezier(0.34, 1.4, 0.64, 1), opacity 260ms ease-out"
            : "transform 380ms cubic-bezier(0.6, 0, 0.78, 0.55), opacity 280ms ease-in",
          willChange: "transform, opacity",
        }}
      >
        {/* Glass card */}
        <div
          role="dialog"
          aria-modal="true"
          className="relative rounded-[32px] overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.04) 45%, rgba(255,255,255,0.02) 60%, rgba(255,255,255,0.10) 100%)",
            backdropFilter: "blur(32px) saturate(180%)",
            WebkitBackdropFilter: "blur(32px) saturate(180%)",
            border: "1px solid rgba(255,255,255,0.22)",
            boxShadow:
              "0 32px 100px -20px rgba(0,0,0,0.55), 0 6px 24px -6px rgba(0,0,0,0.4), inset 0 1.5px 0 rgba(255,255,255,0.55), inset 0 -1px 0 rgba(255,255,255,0.06)",
            isolation: "isolate",
          }}
        >
          {/* Diagonal glare highlight */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-[32px]"
            style={{
              background:
                "linear-gradient(130deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.10) 16%, rgba(255,255,255,0) 32%, rgba(255,255,255,0) 70%, rgba(255,255,255,0.08) 88%, rgba(255,255,255,0.22) 100%)",
            }}
          />

          {/* Top rim light */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-8 top-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.9) 50%, transparent 100%)",
            }}
          />

          {/* Soft colored blooms */}
          <div
            aria-hidden
            className="pointer-events-none absolute -top-24 -left-16 w-72 h-72 rounded-full"
            style={{
              background: "rgba(255,255,255,0.18)",
              filter: "blur(60px)",
            }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-20 -right-10 w-72 h-72 rounded-full"
            style={{
              background: "rgba(212,175,55,0.22)",
              filter: "blur(70px)",
            }}
          />

          {/* Close button */}
          <button
            data-testid="addon-modal-close"
            onClick={handleClose}
            aria-label="Close"
            className="absolute top-5 right-5 z-10 w-10 h-10 rounded-full flex items-center justify-center text-white"
            style={{
              border: "1px solid rgba(255,255,255,0.28)",
              background:
                "linear-gradient(140deg, rgba(255,255,255,0.22), rgba(255,255,255,0.06))",
              boxShadow:
                "inset 0 1px 0 rgba(255,255,255,0.55), 0 4px 12px rgba(0,0,0,0.25)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
            }}
          >
            <X size={16} strokeWidth={2} />
          </button>

          <div className="relative p-8 sm:p-10">
            {/* Icon chip */}
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center text-[#f4d774]"
              style={{
                border: "1px solid rgba(255,255,255,0.30)",
                background:
                  "linear-gradient(140deg, rgba(255,255,255,0.30) 0%, rgba(255,255,255,0.06) 55%, rgba(255,255,255,0.18) 100%)",
                boxShadow:
                  "inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -1px 0 rgba(255,255,255,0.06), 0 4px 14px -4px rgba(0,0,0,0.45)",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
              }}
            >
              {IconComp ? (
                <IconComp width={28} height={28} size={28} strokeWidth={1.5} />
              ) : null}
            </div>

            <h3
              className="mt-7 font-display font-bold text-3xl sm:text-[34px] tracking-tight text-white leading-[1.05]"
              style={{ textShadow: "0 1px 12px rgba(0,0,0,0.5)" }}
            >
              {service.name}
            </h3>

            {/* Price tag(s) */}
            <div className="mt-3.5 flex items-baseline flex-wrap gap-x-2.5 gap-y-1">
              <ServicePrice service={service} />
            </div>

            <p
              className="mt-7 text-[15px] sm:text-base leading-relaxed text-white/95"
              style={{ textShadow: "0 1px 6px rgba(0,0,0,0.45)" }}
            >
              {service.description}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
